/**
 * Catálogos comerciales para Ajustes.
 * Valores alineados a producción (forms / backend actuales).
 * Persistencia local (localStorage) hasta API dedicada.
 * Los catálogos de solicitudes T-COAT se sincronizan con Producción → Ajustes.
 */

import {
  loadProductionCatalogItemsFromStorage,
  saveProductionCatalogItemsToStorage,
} from './productionCatalogDefaults';

export const COMMERCIAL_CATALOG_STORAGE_KEY = 'sqmt_commercial_catalogs_v1';

export const commercialCatalogStorageKey = (unit) => {
  const value = String(unit || '').trim().toLowerCase();
  if (value && value !== 'pinturas' && value !== 'paint') {
    return `${COMMERCIAL_CATALOG_STORAGE_KEY}_${value}`;
  }
  return COMMERCIAL_CATALOG_STORAGE_KEY;
};

const item = (id, code, label, description, sort_order, color_hex, extras = {}) => ({
  id,
  code,
  label,
  description,
  sort_order,
  color_hex,
  is_active: 'Sí',
  parent_code: '',
  parent_label: '',
  ...extras,
});

const codeFromLabel = (label) =>
  String(label || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 40);

export const CATALOG_DEFINITIONS = [
  {
    key: 'segment',
    group: 'leads',
    label: 'Segmentos',
    description: 'Clasificación comercial de organizaciones (B2B, B2G, etc.).',
  },
  {
    key: 'channel',
    group: 'leads',
    label: 'Canales',
    description: 'Canales comerciales usados en organizaciones y cotizaciones.',
  },
  {
    key: 'industry',
    group: 'leads',
    label: 'Industrias',
    description: 'Catálogo de industrias objetivo.',
  },
  {
    key: 'subindustry',
    group: 'leads',
    label: 'Rubros',
    description: 'Rubros / subindustrias asociados a una industria padre.',
    hasParent: true,
    parentCatalogKey: 'industry',
  },
  {
    key: 'funnel_stage',
    group: 'prospecting',
    label: 'Etapas Funnel',
    description: 'Etapas del funnel comercial para prospección y seguimiento.',
  },
  {
    key: 'activity_action',
    group: 'prospecting',
    label: 'Tipos de actividad',
    description: 'Tipos de acciones de contacto / seguimiento.',
  },
  {
    key: 'activity_status',
    group: 'prospecting',
    label: 'Estados actividad',
    description: 'Estados de actividades comerciales.',
  },
  {
    key: 'tender_status',
    group: 'tenders',
    label: 'Estados Licitación',
    description: 'Estados de licitaciones / tenders.',
  },
  {
    key: 'quotation_status',
    group: 'quotes',
    label: 'Estados Cotización',
    description: 'Estados del ciclo de vida de cotizaciones.',
  },
  {
    key: 'payment_terms',
    group: 'quotes',
    label: 'Condiciones de pago',
    description: 'Condiciones de pago usadas en cotizaciones.',
  },
  {
    key: 'loss_reason',
    group: 'quotes',
    label: 'Motivos de pérdida',
    description: 'Motivos por los cuales una cotización se declara Perdida.',
  },
  {
    key: 'sale_status',
    group: 'sales',
    label: 'Estados Venta',
    description: 'Estados de ciclo de vida de ventas.',
  },
  {
    key: 'product_request_status',
    group: 'requests',
    label: 'Estados solicitud T-COAT',
    description:
      'Estados de solicitudes de producto (Comercial → Planificación / Producción). El color se usa en tablas y badges.',
  },
  {
    key: 'product_request_origin',
    group: 'requests',
    label: 'Orígenes de solicitud',
    description: 'Orígenes al crear una solicitud de producto (Comercial, Bodega PT, etc.).',
  },
  {
    key: 'product_request_rejection_reason',
    group: 'requests',
    label: 'Motivos de rechazo',
    description: 'Motivos al rechazar una solicitud desde Planificación.',
  },
  {
    key: 'product_request_item_progress',
    group: 'requests',
    label: 'Avance de ítems',
    description: 'Estados de avance por producto dentro de una solicitud aceptada.',
  },
  {
    key: 'dispatch_status',
    group: 'logistics',
    label: 'Estados Despacho',
    description:
      'Estados operativos de entregas. El color se usa en tabla, badges y calendario.',
  },
  {
    key: 'logistics_provider',
    group: 'logistics',
    label: 'Proveedores Logísticos',
    description:
      'Empresas de transporte disponibles al crear un viaje. El código de dos dígitos se asigna automáticamente y forma parte del correlativo (TRPYY-ZZXXX), por eso no se puede cambiar después.',
    // Vive en la base de datos, no en localStorage: el código alimenta correlativos.
    storage: 'api',
  },
];

export const CATALOG_GROUPS = [
  {
    key: 'leads',
    label: 'Leads',
    description: 'Segmentos, canales, industrias y rubros.',
  },
  {
    key: 'prospecting',
    label: 'Prospección',
    description: 'Funnel y actividades de seguimiento.',
  },
  {
    key: 'tenders',
    label: 'Licitaciones',
    description: 'Estados de licitaciones.',
  },
  {
    key: 'quotes',
    label: 'Cotizaciones',
    description: 'Estados, condiciones de pago y motivos de pérdida.',
  },
  {
    key: 'sales',
    label: 'Ventas',
    description: 'Estados de ciclo de vida de ventas.',
  },
  {
    key: 'requests',
    label: 'Solicitudes',
    description:
      'Estados, orígenes y motivos de solicitudes T-COAT (sincronizados con Producción → Ajustes). Se reflejan en Planificación y Producción.',
  },
  {
    key: 'logistics',
    label: 'Despachos',
    description: 'Estados operativos de entregas.',
  },
  {
    key: 'after_sales',
    label: 'Post-Venta',
    description: 'Parámetros de post-venta.',
  },
];

const INDUSTRY_SEED = [
  'Administración Pública',
  'FFAA',
  'Educación',
  'Salud',
  'Minería',
  'Logística',
  'Energía',
  'Construcción Naval',
  'Alimentos',
  'Construcción',
  'Turismo',
  'Montaje y Mantención',
  'OEM',
  'Modulares',
];

const SUBINDUSTRY_BY_INDUSTRY = {
  'Administración Pública': ['Municipalidades', 'Servicios Públicos', 'Gobierno Central'],
  FFAA: ['Ejército', 'Armada', 'Fuerza Aérea'],
  Educación: ['Universidades', 'Colegios', 'Institutos Técnicos'],
  Salud: ['Hospitales', 'Clínicas', 'Laboratorios'],
  Minería: ['Gran Minería', 'Mediana Minería', 'Servicios Mineros'],
  Logística: ['Transporte Terrestre', 'Centros de Distribución', 'Operadores Logísticos'],
  Energía: ['Generación', 'Transmisión', 'Distribución'],
  'Construcción Naval': ['Astilleros', 'Reparación Naval', 'Proveedores Marítimos'],
  Alimentos: ['Procesamiento', 'Bebidas', 'Cadena Frío'],
  Construcción: ['Inmobiliaria', 'Obras Civiles', 'Terminaciones'],
  Turismo: ['Hotelería', 'Gastronomía', 'Operadores Turísticos'],
  'Montaje y Mantención': ['Mantenimiento Industrial', 'Montaje Electromecánico', 'Paradas de Planta'],
  OEM: ['Fabricantes de Equipos', 'Integradores', 'Postventa OEM'],
  Modulares: ['Habitacionales', 'Industriales', 'Comerciales'],
};

const INDUSTRY_COLORS = [
  '#2563eb', '#0ea5e9', '#8b5cf6', '#10b981', '#7c3aed', '#f59e0b', '#ea580c',
  '#0284c7', '#16a34a', '#f97316', '#db2777', '#0f766e', '#4f46e5', '#64748b',
];

const buildIndustryItems = () =>
  INDUSTRY_SEED.map((label, index) =>
    item(
      index + 1,
      codeFromLabel(label),
      label,
      `Industria: ${label}`,
      (index + 1) * 10,
      INDUSTRY_COLORS[index % INDUSTRY_COLORS.length]
    )
  );

const buildSubindustryItems = () => {
  const rows = [];
  let id = 1;
  INDUSTRY_SEED.forEach((industryLabel, industryIndex) => {
    const parentCode = codeFromLabel(industryLabel);
    const children = SUBINDUSTRY_BY_INDUSTRY[industryLabel] || [];
    children.forEach((label, childIndex) => {
      rows.push(
        item(
          id,
          `${parentCode}__${codeFromLabel(label)}`.slice(0, 48),
          label,
          `Rubro de ${industryLabel}`,
          industryIndex * 100 + (childIndex + 1) * 10,
          '#64748b',
          { parent_code: parentCode, parent_label: industryLabel }
        )
      );
      id += 1;
    });
  });
  return rows;
};

export const QUOTATION_LOSS_REASON_DEFAULTS = [
  item(1, 'PRECIO', 'Precio no competitivo', 'La oferta quedó fuera por precio frente a la competencia.', 10, '#dc2626'),
  item(2, 'PLAZO', 'Plazo de entrega', 'No se cumplió el plazo requerido por el cliente.', 20, '#ea580c'),
  item(3, 'COMPETENCIA', 'Adjudicada a competencia', 'El cliente eligió otra empresa.', 30, '#b91c1c'),
  item(4, 'PRESUPUESTO', 'Sin presupuesto / proyecto cancelado', 'El cliente no ejecutó o canceló el proyecto.', 40, '#7c2d12'),
  item(5, 'ESPECIFICACION', 'Especificación / producto no cumple', 'El producto o la ficha técnica no cumplió requisitos.', 50, '#9a3412'),
  item(6, 'CONDICIONES', 'Condiciones comerciales', 'Pago, crédito u otras condiciones no aceptadas.', 60, '#c2410c'),
  item(7, 'SIN_RESPUESTA', 'Sin respuesta del cliente', 'No hubo feedback tras el envío de la cotización.', 70, '#78716c'),
  item(8, 'OTRO', 'Otro', 'Motivo no listado; detallar en observaciones si aplica.', 90, '#57534e'),
];

export const DEFAULT_CATALOG_ITEMS = {
  segment: [
    item(1, 'B2B', 'B2B', 'Empresa a empresa', 10, '#2563eb'),
    item(2, 'B2C', 'B2C', 'Empresa a consumidor', 20, '#0ea5e9'),
    item(3, 'B2G', 'B2G', 'Empresa a gobierno', 30, '#7c3aed'),
    item(4, 'B2B2C', 'B2B2C', 'Canal mixto', 40, '#8b5cf6'),
    item(5, 'SIN_ASIGNAR', 'Sin Asignar', 'Sin clasificación asignada', 90, '#6b7280'),
  ],
  channel: [
    item(1, 'DIRECTO', 'Directo', 'Gestión comercial directa', 10, '#4f46e5'),
    item(2, 'MP_COMPRA_AGIL', 'M. Público - Compra Ágil', 'Mercado Público · Compra Ágil', 20, '#2563eb'),
    item(3, 'MP_LICITACION', 'M. Público - Licitación', 'Mercado Público · Licitación', 30, '#0284c7'),
    item(4, 'MP_TRATO_DIRECTO', 'M. Público - Trato Directo', 'Mercado Público · Trato Directo', 40, '#7c3aed'),
    item(5, 'SENEGOCIA', 'Senegocia', 'Plataforma Senegocia', 50, '#d97706'),
    item(6, 'WHEREX', 'WhereX', 'Plataforma WhereX', 60, '#8b5cf6'),
  ],
  industry: buildIndustryItems(),
  subindustry: buildSubindustryItems(),
  funnel_stage: [
    item(1, 'IDENTIFICADO', 'Identificado', 'Lead u oportunidad identificada', 10, '#6b7280'),
    item(2, 'CONTACTADO', 'Contactado', 'Primer contacto realizado', 20, '#2563eb'),
    item(3, 'CALIFICADO', 'Calificado', 'Validado comercialmente', 30, '#0ea5e9'),
    item(4, 'PROPUESTA', 'Propuesta', 'Propuesta / cotización en curso', 40, '#8b5cf6'),
    item(5, 'NEGOCIACION', 'Negociación', 'Negociación activa', 50, '#a855f7'),
    item(6, 'CIERRE', 'Cierre', 'Etapa final de cierre', 60, '#16a34a'),
    item(7, 'PERDIDO', 'Perdido', 'Oportunidad perdida en funnel', 70, '#dc2626'),
    item(8, 'ELIMINADO', 'Eliminado', 'Contacto u oportunidad eliminada del funnel', 80, '#52525b'),
  ],
  quotation_status: [
    item(1, 'BORRADOR', 'Borrador', 'En preparación', 10, '#6b7280'),
    item(2, 'EMITIDA', 'Emitida', 'Emitida al cliente', 20, '#2563eb'),
    item(3, 'DESIERTA', 'Desierta', 'Proceso desierto', 30, '#ea580c'),
    item(4, 'ADJUDICADA', 'Adjudicada', 'Aceptada por el cliente', 40, '#16a34a'),
    item(5, 'VENDIDA', 'Vendida', 'Venta confirmada', 50, '#0d9488'),
    item(6, 'CANCELADA', 'Cancelada', 'Cancelada internamente', 60, '#78716c'),
    item(7, 'PERDIDA', 'Perdida', 'No adjudicada', 70, '#dc2626'),
  ],
  payment_terms: [
    item(1, 'CONTADO', 'Contado', 'Pago al contado', 10, '#16a34a'),
    item(2, 'CREDITO_30', 'Credito 30 Dias', 'Crédito 30 días', 20, '#2563eb'),
    item(3, 'CREDITO_45', 'Credito 45 Dias', 'Crédito 45 días', 30, '#0ea5e9'),
    item(4, 'CREDITO_60', 'Credito 60 Dias', 'Crédito 60 días', 40, '#8b5cf6'),
    item(5, 'CREDITO_90', 'Credito 90 Dias', 'Crédito 90 días', 50, '#a855f7'),
    item(6, 'ANTICIPO_50', 'Anticipo 50% / Saldo contra entrega', 'Anticipo 50% y saldo contra entrega', 60, '#f59e0b'),
    item(7, 'ANTICIPO_100', 'Anticipo 100%', 'Anticipo del 100%', 70, '#ea580c'),
    item(8, 'CONTRA_ENTREGA', 'Contra entrega', 'Pago contra entrega', 80, '#0f766e'),
  ],
  loss_reason: QUOTATION_LOSS_REASON_DEFAULTS,
  sale_status: [
    item(1, 'EN_PREPARACION', 'En Preparación', 'Venta en preparación / despacho pendiente', 10, '#f59e0b'),
    item(2, 'EN_RUTA', 'En Ruta', 'Despacho en tránsito', 20, '#ea580c'),
    item(3, 'RECEPCIONADO', 'Recepcionado', 'Entrega confirmada por el cliente', 30, '#4f46e5'),
    item(4, 'FACTURADO', 'Facturado', 'Documento de venta emitido o vinculado', 40, '#2563eb'),
    item(5, 'PAGADO', 'Pagado', 'Venta cobrada / cierre de pago', 50, '#16a34a'),
  ],
  dispatch_status: [
    item(1, 'SOLICITADA', 'Solicitada', 'Pedido requerido a producción, aún no aceptado', 5, '#64748b'),
    item(2, 'PROGRAMADA', 'Programada', 'Entrega planificada, aún no iniciada', 10, '#1d4ed8'),
    item(3, 'REPROGRAMADA', 'Reprogramada', 'Producción propuso nueva fecha; pendiente de comercial', 15, '#9333ea'),
    item(
      4,
      'EN_REPROGRAMACION',
      'En Reprogramación',
      'Solicitud cancelada en producción; pendiente de nueva solicitud',
      18,
      '#7c3aed'
    ),
    item(5, 'EN_RUTA_A_TIEMPO', 'En Ruta a Tiempo', 'En tránsito dentro del plazo prometido', 20, '#c2410c'),
    item(
      6,
      'EN_RUTA_CON_RETRASO',
      'En Ruta con Retraso',
      'En tránsito fuera del plazo prometido',
      30,
      '#9a3412'
    ),
    item(
      7,
      'ENTREGA_CON_INCIDENTE',
      'Entrega Con Incidente',
      'Marcada manualmente con incidente',
      40,
      '#b91c1c'
    ),
    item(8, 'ENTREGA_CONFORME', 'Entrega Conforme', 'Cerrada conforme / a tiempo', 50, '#166534'),
    item(
      9,
      'ENTREGA_CON_RETRASO',
      'Entrega con Retraso',
      'Cerrada fuera del plazo prometido',
      60,
      '#a16207'
    ),
    item(10, 'CANCELADO', 'Cancelado', 'Entrega cancelada (p. ej. rechazo de reprogramación)', 70, '#78716c'),
  ],
  tender_status: [
    item(1, 'ABIERTA', 'Abierta', 'Licitación abierta', 10, '#2563eb'),
    item(2, 'EN_ANALISIS', 'En Analisis', 'En análisis interno', 20, '#0ea5e9'),
    item(3, 'OFERTA_ENVIADA', 'Oferta Enviada', 'Oferta enviada', 30, '#8b5cf6'),
    item(4, 'ADJUDICADA', 'Adjudicada', 'Licitación adjudicada', 40, '#16a34a'),
    item(5, 'PERDIDA', 'Perdida', 'Licitación perdida', 50, '#dc2626'),
    item(6, 'CANCELADA', 'Cancelada', 'Licitación cancelada', 60, '#78716c'),
  ],
  activity_action: [
    item(1, 'LLAMADA', 'Llamada', 'Llamada telefónica', 10, '#2563eb'),
    item(2, 'CORREO', 'Correo', 'Correo electrónico', 20, '#0ea5e9'),
    item(3, 'WHATSAPP', 'WhatsApp', 'Mensaje WhatsApp', 30, '#16a34a'),
    item(4, 'VISITA', 'Visita', 'Visita presencial', 40, '#f59e0b'),
    item(5, 'REUNION', 'Reunión', 'Reunión comercial', 50, '#8b5cf6'),
    item(6, 'OTRO', 'Otro', 'Otra actividad', 90, '#6b7280'),
  ],
  activity_status: [
    item(1, 'PROGRAMADA', 'Programada', 'Actividad programada', 10, '#2563eb'),
    item(2, 'PENDIENTE', 'Pendiente', 'Actividad pendiente', 20, '#f59e0b'),
    item(3, 'COMPLETA', 'Completa', 'Actividad completada', 30, '#16a34a'),
  ],
  product_request_status: [
    item(1, 'SOLICITADO', 'Solicitado', 'Solicitud creada, pendiente de planificación', 10, '#64748b'),
    item(2, 'PLANIFICADA', 'Planificada', 'Aceptada y planificada en producción', 20, '#0ea5e9'),
    item(3, 'EN_PROCESO', 'En Proceso', 'En fabricación / proceso', 30, '#d97706'),
    item(4, 'TERMINADA', 'Terminada', 'Fabricación terminada', 40, '#15803d'),
    item(5, 'ENTREGADA', 'Entregada', 'Entregada a comercial / logística', 50, '#0f766e'),
    item(6, 'RECHAZADA', 'Rechazada', 'Rechazada por producción', 60, '#dc2626'),
    item(7, 'EN_REPROGRAMACION', 'En Reprogramación', 'Pendiente de nueva fecha', 70, '#9333ea'),
    item(8, 'CANCELADA', 'Cancelada', 'Cancelada', 80, '#71717a'),
  ],
  product_request_origin: [
    item(1, 'COMERCIAL', 'Comercial', 'Generada desde Comercial', 10, '#2563eb'),
    item(2, 'BODEGA_PT', 'Bodega PT', 'Generada desde bodega de producto terminado', 20, '#0ea5e9'),
    item(3, 'STOCK_MINIMO', 'Stock mínimo', 'Reposición por stock mínimo', 30, '#f59e0b'),
    item(4, 'MUESTRA', 'Muestra', 'Solicitud de muestra', 40, '#8b5cf6'),
    item(5, 'REPOSICION', 'Reposición', 'Reposición general', 50, '#16a34a'),
  ],
  product_request_rejection_reason: [
    item(1, 'FALTA_MP', 'Falta de materia prima', 'No hay stock de materias primas', 10, '#dc2626'),
    item(2, 'FORMULA', 'Fórmula o color no disponible', 'Fórmula o color no está disponible', 20, '#ea580c'),
    item(3, 'CAPACIDAD', 'Capacidad de producción insuficiente', 'No hay capacidad en la planta', 30, '#c2410c'),
    item(4, 'FECHA', 'Fecha límite no factible', 'La fecha límite no es alcanzable', 40, '#b45309'),
    item(5, 'INFO', 'Información incompleta o incorrecta', 'Faltan datos para fabricar', 50, '#a16207'),
    item(6, 'DUPLICADA', 'Solicitud duplicada', 'Ya existe una solicitud equivalente', 60, '#78716c'),
    item(7, 'NO_FABRICABLE', 'Producto no fabricable', 'El producto no se puede fabricar', 70, '#9a3412'),
    item(8, 'OTRO', 'Otro', 'Otro motivo; detallar en observaciones', 90, '#57534e'),
  ],
  product_request_item_progress: [
    item(1, 'PLANIFICADO', 'Planificado', 'Ítem planificado, aún no en fabricación', 10, '#0ea5e9'),
    item(2, 'EN_FABRICACION', 'En Fabricación', 'Ítem en proceso de fabricación', 20, '#d97706'),
    item(3, 'TERMINADO', 'Terminado', 'Ítem terminado', 30, '#15803d'),
  ],
};

export const COMMERCIAL_CATALOGS_CHANGED_EVENT = 'sqmt-commercial-catalogs-changed';

/** Keys shared with Producción → Ajustes (single source of truth in production storage). */
export const PRODUCTION_SYNC_CATALOG_KEYS = [
  'product_request_status',
  'product_request_origin',
  'product_request_rejection_reason',
  'product_request_item_progress',
];

const activeCatalogRows = (key, items, unit) => {
  const catalog = items != null ? null : loadCatalogItemsFromStorage(unit) || DEFAULT_CATALOG_ITEMS;
  const source = Array.isArray(items)
    ? items
    : (catalog && catalog[key]) || DEFAULT_CATALOG_ITEMS[key] || [];
  return (Array.isArray(source) ? source : [])
    .filter((row) => String(row.is_active || 'Sí').toLowerCase() === 'sí')
    .slice()
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
};

const rowsToLabelOptions = (rows) =>
  rows
    .map((row) => ({
      value: String(row.label || row.code || '').trim(),
      label: String(row.label || row.code || '').trim(),
      code: row.code,
      color_hex: row.color_hex || '#2563eb',
      sort_order: Number(row.sort_order || 0),
    }))
    .filter((opt) => opt.value);

export const getActiveProductRequestStatusRows = (items, unit) =>
  activeCatalogRows('product_request_status', items, unit);

export const getActiveProductRequestStatusOptions = (items, unit) =>
  rowsToLabelOptions(getActiveProductRequestStatusRows(items, unit));

export const getActiveProductRequestOriginOptions = (items, unit) =>
  rowsToLabelOptions(activeCatalogRows('product_request_origin', items, unit));

export const getActiveProductRequestRejectionReasonOptions = (items, unit) =>
  rowsToLabelOptions(activeCatalogRows('product_request_rejection_reason', items, unit));

export const getActiveProductRequestItemProgressOptions = (items, unit) =>
  rowsToLabelOptions(activeCatalogRows('product_request_item_progress', items, unit));

/** Mapa label → color_hex para badges de solicitudes T-COAT en Producción. */
export const getProductRequestStatusColorMap = (items, unit) => {
  const map = {};
  getActiveProductRequestStatusRows(items, unit).forEach((row) => {
    const label = String(row.label || '').trim();
    if (!label) return;
    map[label] = row.color_hex || '#64748b';
  });
  return map;
};

export const getActiveQuotationLossReasonOptions = (items = QUOTATION_LOSS_REASON_DEFAULTS) =>
  (Array.isArray(items) ? items : [])
    .filter((row) => String(row.is_active || 'Sí').toLowerCase() === 'sí')
    .slice()
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .map((row) => ({
      value: String(row.label || row.code || '').trim(),
      label: String(row.label || row.code || '').trim(),
      code: row.code,
    }))
    .filter((opt) => opt.value);

export const loadCatalogItemsFromStorage = (unit) => {
  try {
    const raw = window.localStorage.getItem(commercialCatalogStorageKey(unit));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const merged = { ...DEFAULT_CATALOG_ITEMS };
    Object.keys(DEFAULT_CATALOG_ITEMS).forEach((key) => {
      if (Array.isArray(parsed[key])) merged[key] = parsed[key];
    });
    // Si el catálogo de canales guardado es el antiguo, reemplazar por los nuevos.
    const storedChannels = Array.isArray(parsed.channel) ? parsed.channel : [];
    const hasUpdatedChannels = storedChannels.some((row) => {
      const label = String(row?.label || '').trim();
      const code = String(row?.code || '').trim().toUpperCase();
      return (
        label === 'WhereX' ||
        code === 'WHEREX' ||
        label.startsWith('M. Público') ||
        code.startsWith('MP_')
      );
    });
    if (!hasUpdatedChannels) {
      merged.channel = DEFAULT_CATALOG_ITEMS.channel;
    }
    // Migrar estados de despacho legacy (Pendiente / Programado / En Ruta…) al set operativo actual.
    const storedDispatch = Array.isArray(parsed.dispatch_status) ? parsed.dispatch_status : [];
    const hasOperationalDispatch = storedDispatch.some((row) => {
      const label = String(row?.label || '').trim();
      const code = String(row?.code || '').trim().toUpperCase();
      return (
        label === 'Programada' ||
        label === 'En Ruta a Tiempo' ||
        code === 'PROGRAMADA' ||
        code === 'EN_RUTA_A_TIEMPO'
      );
    });
    const hasSolicitadaDispatch = storedDispatch.some((row) => {
      const label = String(row?.label || '').trim();
      const code = String(row?.code || '').trim().toUpperCase();
      return label === 'Solicitada' || code === 'SOLICITADA';
    });
    if (!hasOperationalDispatch || !hasSolicitadaDispatch) {
      merged.dispatch_status = DEFAULT_CATALOG_ITEMS.dispatch_status;
    }
    // Migrar estados de actividad: Programada / Pendiente / Completa.
    const storedActivityStatus = Array.isArray(parsed.activity_status) ? parsed.activity_status : [];
    const hasProgramadaActivityStatus = storedActivityStatus.some((row) => {
      const label = String(row?.label || '').trim();
      const code = String(row?.code || '').trim().toUpperCase();
      return label === 'Programada' || code === 'PROGRAMADA';
    });
    if (!hasProgramadaActivityStatus) {
      merged.activity_status = DEFAULT_CATALOG_ITEMS.activity_status;
    }
    // Solicitudes T-COAT: fuente de verdad en Producción → Ajustes.
    const productionCatalog = loadProductionCatalogItemsFromStorage();
    PRODUCTION_SYNC_CATALOG_KEYS.forEach((key) => {
      if (Array.isArray(productionCatalog[key]) && productionCatalog[key].length) {
        merged[key] = productionCatalog[key];
      } else if (!Array.isArray(parsed[key]) || !parsed[key].length) {
        merged[key] = DEFAULT_CATALOG_ITEMS[key];
      }
    });
    return merged;
  } catch {
    return null;
  }
};

export const getActiveFunnelStageOptions = (items, unit) => {
  const catalog = items != null ? null : loadCatalogItemsFromStorage(unit) || DEFAULT_CATALOG_ITEMS;
  const source = Array.isArray(items)
    ? items
    : (catalog && catalog.funnel_stage) || DEFAULT_CATALOG_ITEMS.funnel_stage;
  return (Array.isArray(source) ? source : [])
    .filter((row) => String(row.is_active || 'Sí').toLowerCase() === 'sí')
    .slice()
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .map((row) => ({
      value: String(row.label || row.code || '').trim(),
      label: String(row.label || row.code || '').trim(),
      code: row.code,
      color: row.color || '',
      sort_order: Number(row.sort_order || 0),
    }))
    .filter((opt) => opt.value);
};

export const getActiveIndustryOptions = (items, unit) => {
  const catalog = items != null ? null : loadCatalogItemsFromStorage(unit) || DEFAULT_CATALOG_ITEMS;
  const source = Array.isArray(items)
    ? items
    : (catalog && catalog.industry) || DEFAULT_CATALOG_ITEMS.industry;
  return (Array.isArray(source) ? source : [])
    .filter((row) => String(row.is_active || 'Sí').toLowerCase() === 'sí')
    .slice()
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .map((row) => ({
      value: String(row.label || row.code || '').trim(),
      label: String(row.label || row.code || '').trim(),
      code: row.code,
    }))
    .filter((opt) => opt.value);
};

export const getActiveSubindustryOptions = (industryValue, unit) => {
  const catalog = loadCatalogItemsFromStorage(unit) || DEFAULT_CATALOG_ITEMS;
  const industries = Array.isArray(catalog.industry) ? catalog.industry : DEFAULT_CATALOG_ITEMS.industry;
  const parent = industries.find(
    (row) =>
      String(row.label || '').trim() === String(industryValue || '').trim() ||
      String(row.code || '').trim() === String(industryValue || '').trim()
  );
  const parentCode = String(parent?.code || '').trim();
  const parentLabel = String(parent?.label || industryValue || '').trim();
  return (Array.isArray(catalog.subindustry) ? catalog.subindustry : [])
    .filter((row) => String(row.is_active || 'Sí').toLowerCase() === 'sí')
    .filter(
      (row) =>
        (parentCode && String(row.parent_code || '').trim() === parentCode) ||
        (parentLabel && String(row.parent_label || '').trim() === parentLabel)
    )
    .slice()
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .map((row) => ({
      value: String(row.label || row.code || '').trim(),
      label: String(row.label || row.code || '').trim(),
      code: row.code,
    }))
    .filter((opt) => opt.value);
};

export const getActiveChannelOptions = (items, unit) => {
  const catalog = items != null ? null : loadCatalogItemsFromStorage(unit) || DEFAULT_CATALOG_ITEMS;
  const source = Array.isArray(items)
    ? items
    : (catalog && catalog.channel) || DEFAULT_CATALOG_ITEMS.channel;
  return (Array.isArray(source) ? source : [])
    .filter((row) => String(row.is_active || 'Sí').toLowerCase() === 'sí')
    .slice()
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .map((row) => ({
      value: String(row.label || row.code || '').trim(),
      label: String(row.label || row.code || '').trim(),
      code: row.code,
    }))
    .filter((opt) => opt.value);
};

/** Filas activas del catálogo Estados Despacho (Ajustes → Operaciones). */
export const getActiveDispatchStatusRows = (items, unit) => {
  const catalog = items != null ? null : loadCatalogItemsFromStorage(unit) || DEFAULT_CATALOG_ITEMS;
  const source = Array.isArray(items)
    ? items
    : (catalog && catalog.dispatch_status) || DEFAULT_CATALOG_ITEMS.dispatch_status;
  return (Array.isArray(source) ? source : [])
    .filter((row) => String(row.is_active || 'Sí').toLowerCase() === 'sí')
    .slice()
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
};

export const getActiveDispatchStatusOptions = (items, unit) =>
  getActiveDispatchStatusRows(items, unit)
    .map((row) => ({
      value: String(row.label || row.code || '').trim(),
      label: String(row.label || row.code || '').trim(),
      code: row.code,
      color_hex: row.color_hex || '#2563eb',
      sort_order: Number(row.sort_order || 0),
    }))
    .filter((opt) => opt.value);

export const saveCatalogItemsToStorage = (itemsByKey, unit) => {
  try {
    window.localStorage.setItem(commercialCatalogStorageKey(unit), JSON.stringify(itemsByKey));
    // Mantener Producción → Ajustes al día cuando se editan solicitudes desde Comercial.
    const productionCatalog = loadProductionCatalogItemsFromStorage();
    let synced = false;
    PRODUCTION_SYNC_CATALOG_KEYS.forEach((key) => {
      if (Array.isArray(itemsByKey?.[key])) {
        productionCatalog[key] = itemsByKey[key];
        synced = true;
      }
    });
    if (synced) saveProductionCatalogItemsToStorage(productionCatalog);
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(
        new CustomEvent(COMMERCIAL_CATALOGS_CHANGED_EVENT, {
          detail: { unit: unit || 'pinturas', keys: Object.keys(itemsByKey || {}) },
        })
      );
    }
  } catch {
    // ignore quota / private mode
  }
};

export { codeFromLabel };
